import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaAward,
  FaBuilding,
  FaChevronRight,
  FaClock,
  FaCode,
  FaCogs,
  FaComments,
  FaFileAlt,
  FaHandshake,
  FaHeadset,
  FaLandmark,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";

import Navbar from "../components/Navbar5";
import Footer from "../components/Footer1";
import styles from "../styles/GlobalSoftware.module.css";
import ConsultationModal from "../components/ConsultationModal";
import SEO from "../components/SEO";

const globalSoftwareSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Global Software Development Services",
  url: "https://www.vishvaconnect.com/global-software-services",
  description:
    "International software development, offshore development, automation solutions, white-label software development and dedicated development partnerships.",
  provider: {
    "@type": "Organization",
    name: "VISHVACONNECT TECHNOLOGIES LLP",
    url: "https://www.vishvaconnect.com/",
    email: "vishvaconnect7@gmail.com",
  },
  areaServed: [
    {
      "@type": "Country",
      name: "India",
    },
    {
      "@type": "Country",
      name: "United Kingdom",
    },
    {
      "@type": "Country",
      name: "United Arab Emirates",
    },
    {
      "@type": "AdministrativeArea",
      name: "Worldwide",
    },
  ],
  serviceType: [
    "Custom Software Development",
    "Offshore Software Development",
    "White-Label Software Development",
    "Workflow Automation",
    "Dedicated Development Team",
  ],
};

const benefits = [
  {
    title: "Cost Effective",
    subtitle: "Solutions",
    icon: FaShieldAlt,
  },
  {
    title: "High Quality",
    subtitle: "Delivery",
    icon: FaAward,
  },
  {
    title: "On-Time",
    subtitle: "Always",
    icon: FaClock,
  },
  {
    title: "Long-Term",
    subtitle: "Partnership",
    icon: FaHandshake,
  },
];

const globalServices = [
  {
    title: "Software Development\nPartner India",
    description:
      "End-to-end software development for startups, SMEs and enterprises worldwide.",
    icon: FaCode,
  },
  {
    title: "UK Software\nDevelopment Partner",
    description:
      "Dedicated development partner for UK businesses with time zone compatibility.",
    icon: FaLandmark,
  },
  {
    title: "UAE Software\nAutomation Solutions",
    description:
      "Smart automation and digital solutions for businesses across UAE and the Middle East.",
    icon: FaBuilding,
  },
  {
    title: "White-Label Software\nDevelopment",
    description:
      "We work as your technology partner behind the scenes. You take the credit, we do the magic.",
    icon: FaHandshake,
  },
];

const selectedWork = [
  {
    title: "Government Reporting Platform",
    description:
      "Real-time reporting and monitoring system for government departments.",
    action: "Explore Service",
    image: "/card1.png",
  },
  {
    title: "WhatsApp Automation System",
    description:
      "Automated communication, reporting and alert system via WhatsApp.",
    action: "Explore Service",
    image: "/card2.png",
  },
  {
    title: "Citizen Service Portal",
    description:
      "Online citizen services portal with secure application and approval workflows.",
    action: "View Case Study",
    image: "/card3.png",
  },
];

const whyWorkItems = [
  {
    title: "Clear Communication",
    description: "We keep you updated at every step.",
    icon: FaComments,
  },
  {
    title: "Secure & IP Protected",
    description: "Your ideas and data are 100% safe with us.",
    icon: FaShieldAlt,
  },
  {
    title: "Flexible Engagement",
    description: "Choose the model that fits your goals and budget.",
    icon: FaCogs,
  },
  {
    title: "Long-Term Support",
    description: "We build relationships, not just software.",
    icon: FaHeadset,
  },
];

const engagementModels = [
  {
    title: "Fixed Price Project",
    description:
      "Best for well-defined requirements with clear scope and timeline.",
    icon: FaFileAlt,
  },
  {
    title: "Dedicated Development Team",
    description:
      "Hire dedicated developers for long-term projects and continuous development.",
    icon: FaUsers,
  },
  {
    title: "White-Label Partnership",
    description:
      "We work under your brand and deliver solutions to your clients.",
    icon: FaShieldAlt,
  },
];

function GlobalSoftware() {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);

  return (
    <div className={styles.page}>
      <SEO
        title="Global Software Development Partner in India | VishvaConnect"
        description="Partner with VishvaConnect for international software development, offshore teams, white-label development and automation solutions for UK, UAE and global businesses."
        path="/global-software-services"
        schema={globalSoftwareSchema}
      />
      <Navbar />

      <main className={styles.main}>
        <section className={styles.heroSection}>
          <div className={styles.mapVisual} aria-hidden="true"></div>
          <div className={styles.heroOverlay} aria-hidden="true"></div>

          <div className={styles.heroInner}>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaChevronRight className={styles.breadcrumbIcon} />
              <span>Global Services</span>
            </nav>

            <div className={styles.content}>
              <h1 className={styles.heading}>
                <span>Global Software Solutions.</span>
                <span>
                  <strong>Built in India.</strong> Delivered Worldwide.
                </span>
              </h1>

              <p className={styles.description}>
                Partner with VishvaConnect Technologies LLP for custom software
                development, automation solutions and long-term technical
                partnership that helps your business grow.
              </p>

              <div className={styles.benefits} aria-label="Key benefits">
                {benefits.map((benefit) => {
                  const BenefitIcon = benefit.icon;

                  return (
                    <div className={styles.benefitItem} key={benefit.title}>
                      <BenefitIcon className={styles.benefitIcon} />
                      <span>
                        <strong>{benefit.title}</strong>
                        <small>{benefit.subtitle}</small>
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => setIsConsultationModalOpen(true)}
                >
                  <span>Book a Free Consultation</span>
                  <FaArrowRight className={styles.buttonIcon} />
                </button>

                <Link to="/contact" className={styles.secondaryButton}>
                  <span>Share Your Requirements</span>
                  <FaArrowRight className={styles.buttonIcon} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.globalServicesSection}>
          <div className={styles.globalServicesInner}>
            <div className={styles.sectionHeader}>
              <h2>Our Global Services</h2>
              <span className={styles.sectionUnderline}></span>
              <p>
                Custom solutions designed to meet your goals and scale your
                business.
              </p>
            </div>

            <div className={styles.serviceGrid}>
              {globalServices.map((service) => {
                const ServiceIcon = service.icon;

                return (
                  <article
                    className={styles.globalServiceCard}
                    key={service.title}
                  >
                    <div className={styles.globalServiceIcon}>
                      <ServiceIcon />
                    </div>

                    <h3>{service.title}</h3>

                    <p>{service.description}</p>

                    <button
                      type="button"
                      className={styles.exploreLink}
                      onClick={() => setIsConsultationModalOpen(true)}
                    >
                      <span>Explore Services</span>
                      <FaArrowRight />
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className={styles.selectedWorkSection}>
          <div className={styles.selectedWorkInner}>
            <div className={styles.sectionHeader}>
              <h2>Selected Work</h2>
              <span className={styles.sectionUnderline}></span>
              <p>
                A glimpse of solutions we have delivered to clients across
                industries.
              </p>
            </div>

            <div className={styles.selectedWorkGrid}>
              {selectedWork.map((project) => (
                <article className={styles.workCard} key={project.title}>
                  <div className={styles.workPreview}>
                    <img
                      src={project.image}
                      alt={`${project.title} project dashboard`}
                      className={styles.workImage}
                      loading="lazy"
                    />
                  </div>

                  <div className={styles.workContent}>
                    <h3>{project.title}</h3>

                    <p>{project.description}</p>

                    <button
                      type="button"
                      className={styles.workAction}
                      onClick={() => setIsConsultationModalOpen(true)}
                    >
                      <span>{project.action}</span>
                      <FaArrowRight />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.partnerPanelSection}>
          <div className={styles.partnerPanel}>
            <div className={styles.whyColumn}>
              <h2 className={styles.panelTitle}>
                Why Work With VishvaConnect?
              </h2>
              <span className={styles.panelUnderline}></span>

              <div className={styles.whyGrid}>
                {whyWorkItems.map((item) => {
                  const ItemIcon = item.icon;

                  return (
                    <div className={styles.whyItem} key={item.title}>
                      <ItemIcon className={styles.whyIcon} />

                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.engagementColumn}>
              <h2 className={styles.panelTitle}>Engagement Models</h2>
              <p className={styles.engagementIntro}>
                Choose the engagement model that works best for your project.
              </p>

              <div className={styles.engagementGrid}>
                {engagementModels.map((model) => {
                  const ModelIcon = model.icon;

                  return (
                    <article
                      className={styles.engagementCard}
                      key={model.title}
                    >
                      <ModelIcon className={styles.engagementIcon} />
                      <h3>{model.title}</h3>
                      <p>{model.description}</p>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className={styles.finalCtaPanel}>
          <div className={styles.finalCtaIcon}>
            <FaHeadset />
          </div>

          <div className={styles.finalCtaText}>
            <h2>Need a Reliable Software Development Partner?</h2>
            <p>
              Let&apos;s discuss how we can help you build, automate and grow
              your business.
            </p>
          </div>

          <div className={styles.finalCtaActions}>
            <button
              onClick={() => setIsConsultationModalOpen(true)}
              className={styles.finalPrimaryButton}
            >
              <span>Book a Free Consultation</span>
              <FaArrowRight />
            </button>

            <Link to="/contact" className={styles.finalSecondaryButton}>
              <span>Share Your Requirements</span>
              <FaArrowRight />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
      />
    </div>
  );
}

export default GlobalSoftware;
